/// <amd-module name="Repos/Views/PullRequest/Util/Diff" />
import { IChange, IFileDiff, IFileDiffParameters } from "Repos/ChangesCommon/Contracts";
import { GitPullRequestCommentThread, GitVersionDescriptor, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { branchToVersionDescriptor, getVersionString } from "Repos/Common/Util/Version";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";

export function getFileDiffParameters(
    pageContext: IVssPageContext,
    filePath: string,
    sourceFilePath: string | undefined,
    changeType: VersionControlChangeType,
    originalVersionDescriptor?: GitVersionDescriptor,
    modifiedVersionDescriptor?: GitVersionDescriptor,
    includeCharDiffs: boolean = true
): IFileDiffParameters {
    const prDetailsService = getPullRequestViewService(pageContext);

    const originalVersion = originalVersionDescriptor
        ? getVersionString(originalVersionDescriptor)
        : getVersionString(branchToVersionDescriptor(prDetailsService.getTargetBranch().value));

    const modifiedVersion = modifiedVersionDescriptor
        ? getVersionString(modifiedVersionDescriptor)
        : getVersionString(branchToVersionDescriptor(prDetailsService.getSourceBranch()));

    let originalPath = sourceFilePath || filePath;
    if ((changeType & VersionControlChangeType.Add) === VersionControlChangeType.Add) {
        originalPath = "";
    }

    let modifiedPath = filePath;
    if ((changeType & VersionControlChangeType.Delete) === VersionControlChangeType.Delete) {
        modifiedPath = "";
    }

    return {
        includeCharDiffs: includeCharDiffs,
        modifiedPath: modifiedPath,
        modifiedVersion: modifiedVersion,
        originalPath: originalPath,
        originalVersion: originalVersion,
        partialDiff: false
    };
}

export function getFileDiff(
    pageContext: IVssPageContext,
    filePath: string,
    sourceFilePath: string | undefined,
    changeType: VersionControlChangeType,
    originalVersionDescriptor?: GitVersionDescriptor,
    modifiedVersionDescriptor?: GitVersionDescriptor,
    includeCharDiffs: boolean = true
): IReadonlyObservableValue<IFileDiff | undefined> {
    const diffParameters = getFileDiffParameters(
        pageContext,
        filePath,
        sourceFilePath,
        changeType,
        originalVersionDescriptor,
        modifiedVersionDescriptor,
        includeCharDiffs
    );
    const gitFilesService = pageContext.getService<IGitFilesService>("IGitFilesService");
    return gitFilesService.getFileDiff(diffParameters);
}

export async function getDiscussionThreadFileDiff(
    pageContext: IVssPageContext,
    thread: GitPullRequestCommentThread,
    change: IChange,
    showOriginalDiff: boolean
): Promise<IReadonlyObservableValue<IFileDiff | undefined>> {
    const prDetailsService = getPullRequestViewService(pageContext);
    let iterationId = prDetailsService.getLatestIterationId();
    let baseIterationId = 0;
    const { iterationContext, trackingCriteria } = thread.pullRequestThreadContext;
    if (!iterationContext || !prDetailsService.getIterationsSupport()) {
        showOriginalDiff = false;
    }

    if (showOriginalDiff) {
        baseIterationId = iterationContext.firstComparingIteration;
        iterationId = iterationContext.secondComparingIteration;
        if (baseIterationId === iterationId) {
            baseIterationId = 0;
        }
    }

    const { originalVersion, modifiedVersion } = prDetailsService.getVersionsForIterationRange(iterationId, baseIterationId);
    if (showOriginalDiff) {
        const preChanges = await prDetailsService.getIterationChanges(iterationId, baseIterationId);
        if (preChanges) {
            const commentPath = trackingCriteria?.origFilePath || change.path;
            change = preChanges.changesByPath[commentPath] || change;
        }
    }

    return getFileDiff(pageContext, change.path, change.sourcePath, change.changeType, originalVersion, modifiedVersion, true);
}
